import { getMediaUrl } from '@/utils/mediaUtils';
import { CMSFooter } from '@travelwin/cms/types';
import { Icon, IconName, isDefaultBrand, useConfig } from '@travelwin/core';
import React from 'react';

interface SocialItem {
  name: string;
  url: string;
  icon: IconName;
}

interface SocialPlatformsProps {
  socialPlatforms: CMSFooter['socialPlatforms'];
  className?: string;
}

const SocialPlatforms: React.FC<SocialPlatformsProps> = ({
  socialPlatforms,
  className,
}) => {
  const { WHITELABEL_BRAND } = useConfig();

  // Fallback to legacy hardcoded social links if no CMS data
  const legacySocialLinks: SocialItem[] = [
    {
      name: 'facebook',
      url: 'https://www.facebook.com/simlocal/',
      icon: 'facebook',
    },
    {
      name: 'instagram',
      url: 'https://www.instagram.com/simlocal/?hl=en-gb',
      icon: 'instagram',
    },
    {
      name: 'x',
      url: 'https://x.com/simlocal',
      icon: 'x',
    },
    {
      name: 'linkedIn',
      url: 'https://www.linkedin.com/company/sim-local',
      icon: 'linkedIn',
    },
    {
      name: 'youtube',
      url: 'https://www.youtube.com/@simlocal_esim',
      icon: 'youtube',
    },
    {
      name: 'tikTok',
      url: 'https://www.tiktok.com/@simlocal_esim',
      icon: 'tikTok',
    },
  ];

  if (!socialPlatforms || socialPlatforms.length === 0) {
    if (!isDefaultBrand(WHITELABEL_BRAND)) {
      return null;
    }

    // If default brand and no CMS data, use legacy fallback
    return (
      <div className={`${className}__sns`}>
        {legacySocialLinks.map((social) => (
          <a
            key={social.name}
            aria-label={social.name}
            href={social.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon name={social.icon} />
          </a>
        ))}
      </div>
    );
  }

  return (
    <div className={`${className}__sns`}>
      {socialPlatforms?.map((platform) => {
        const platformName =
          platform.name || getPlatformNameFromUrl(platform.url);

        return (
          <a
            key={platform.id}
            aria-label={platformName}
            href={platform.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={
                typeof platform.icon === 'object'
                  ? getMediaUrl(platform.icon)
                  : ''
              }
              alt={platformName}
              width={24}
              height={24}
              loading="lazy"
            />
          </a>
        );
      })}
    </div>
  );
};

function getPlatformNameFromUrl(url: string): string {
  try {
    const hostname = new URL(url).hostname.toLowerCase();

    // Common social platform mappings
    const platformMappings: Record<string, string> = {
      'facebook.com': 'Facebook',
      'twitter.com': 'Twitter',
      'x.com': 'Twitter',
      'instagram.com': 'Instagram',
      'linkedin.com': 'LinkedIn',
      'youtube.com': 'YouTube',
      'tiktok.com': 'TikTok',
    };

    for (const [domain, platform] of Object.entries(platformMappings)) {
      if (hostname.includes(domain)) {
        return platform;
      }
    }

    // Extract domain name as fallback
    return hostname.replace('www.', '').split('.')[0];
  } catch {
    return 'Social';
  }
}

export default SocialPlatforms;
